import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, User } from '../api/admin';
import { accountsApi } from '../api/accounts';
import PremiumLayout from '../components/PremiumLayout';
import { PlusIcon, PencilIcon, TrashIcon, UserCircleIcon, ShieldCheckIcon, CheckCircleIcon, XCircleIcon, EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const Admin: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // #region agent log
  console.log('[ADMIN] Component render - currentUser:', {
    exists: !!currentUser,
    id: currentUser?.id,
    name: currentUser?.name,
    email: currentUser?.email,
    role: currentUser?.role
  });
  // #endregion
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showSMTPModal, setShowSMTPModal] = useState(false);
  const [smtpFormData, setSmtpFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    email_from: '',
    escalation_email: '',
    imap_host: '',
    imap_port: 993,
    is_active: true,
  });
  const [testEmail, setTestEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'bd',
    preferred_currency: 'INR' as 'USD' | 'INR',
    notification_email: '',
    reports_to: undefined as number | undefined,
    is_active: true,
    create_with_password: false,
    password: '',
    confirm_password: '',
  });
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const createdCredentialsRef = useRef<{ email: string; password: string } | null>(null);

  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminApi.getUsers,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  // SMTP Settings query (superadmin only)
  const { data: smtpSettings, isLoading: smtpLoading } = useQuery({
    queryKey: ['smtp-settings'],
    queryFn: adminApi.getSMTPSettings,
    enabled: currentUser?.role === 'superadmin',
  });

  // Load SMTP settings into form when data is available
  useEffect(() => {
    if (smtpSettings) {
      setSmtpFormData({
        smtp_host: smtpSettings.smtp_host || '',
        smtp_port: smtpSettings.smtp_port || 587,
        smtp_user: smtpSettings.smtp_user || '',
        smtp_password: '', // Don't populate password
        email_from: smtpSettings.email_from || '',
        escalation_email: smtpSettings.escalation_email || '',
        imap_host: smtpSettings.imap_host || '',
        imap_port: smtpSettings.imap_port || 993,
        is_active: smtpSettings.is_active !== false,
      });
    }
  }, [smtpSettings]);

  // Ensure default reports_to is set when creating a new user
  useEffect(() => {
    // Only run when creating (not editing) and modal is open
    if (showModal && !editingUser && currentUser?.id && currentUser?.role) {
      const eligibleRoles = ['superadmin', 'admin', 'leadership'];
      const currentUserRoleLower = (currentUser.role || '').toLowerCase();
      const isEligible = eligibleRoles.some(r => r.toLowerCase() === currentUserRoleLower);
      
      // #region agent log
      console.log('[ADMIN] useEffect - Setting default reports_to:', {
        showModal,
        isEditing: !!editingUser,
        currentUserId: currentUser.id,
        currentUserName: currentUser.name,
        currentUserRole: currentUser.role,
        isEligible,
        currentReportsTo: formData.reports_to
      });
      // #endregion
      
      if (isEligible) {
        // ALWAYS set reports_to to current user when creating (don't check if already set)
        // This ensures it's set even if resetForm didn't work properly
        setFormData(prev => {
          // Only update if it's different to avoid unnecessary re-renders
          if (prev.reports_to !== currentUser.id) {
            // #region agent log
            console.log('[ADMIN] ✅ Setting reports_to to current user:', currentUser.id);
            // #endregion
            return {
              ...prev,
              reports_to: currentUser.id
            };
          }
          // #region agent log
          console.log('[ADMIN] ✅ reports_to already set to current user:', prev.reports_to);
          // #endregion
          return prev;
        });
      } else {
        // #region agent log
        console.log('[ADMIN] ❌ Current user is NOT eligible to be manager - role:', currentUser.role);
        // #endregion
      }
    } else {
      // #region agent log
      console.log('[ADMIN] ⚠️ useEffect skipped:', {
        showModal,
        hasEditingUser: !!editingUser,
        hasCurrentUser: !!currentUser,
        currentUserId: currentUser?.id,
        currentUserRole: currentUser?.role
      });
      // #endregion
    }
  }, [showModal, editingUser, currentUser?.id, currentUser?.role, currentUser?.name]);

  const potentialManagers = useMemo(() => {
    // #region agent log
    console.log('[ADMIN] potentialManagers calculation START:', {
      usersCount: users.length,
      currentUserId: currentUser?.id,
      currentUserName: currentUser?.name,
      currentUserEmail: currentUser?.email,
      currentUserRole: currentUser?.role,
      editingUserId: editingUser?.id,
      isCreating: !editingUser,
      usersList: users.map(u => ({ id: u.id, name: u.name, role: u.role }))
    });
    // #endregion
    
    // Eligible roles for managers
    const eligibleRoles = ['superadmin', 'admin', 'leadership'];
    
    // #region agent log
    console.log('[ADMIN] Current user eligibility check:', {
      hasCurrentUser: !!currentUser,
      hasCurrentUserId: !!currentUser?.id,
      hasCurrentUserRole: !!currentUser?.role,
      currentUserRole: currentUser?.role,
      isEligible: currentUser?.role && eligibleRoles.includes(currentUser.role),
      eligibleRoles
    });
    // #endregion
    
    // Start with all users, but exclude the user being edited (can't report to self when editing)
    let filtered = users.filter(u => {
      if (editingUser && u.id === editingUser.id) {
        return false; // Can't report to self when editing
      }
      return true;
    });
    
    // #region agent log
    console.log('[ADMIN] After filtering out editing user:', { filteredCount: filtered.length });
    // #endregion
    
    // Filter by eligible roles (case-insensitive)
    filtered = filtered.filter(u => {
      const userRoleLower = (u.role || '').toLowerCase();
      return eligibleRoles.some(r => r.toLowerCase() === userRoleLower);
    });
    
    // #region agent log
    console.log('[ADMIN] After filtering by eligible roles:', { filteredCount: filtered.length, filtered: filtered.map(u => ({ id: u.id, name: u.name, role: u.role })) });
    // #endregion
    
    // CRITICAL FIX: When creating a new user, ALWAYS include current user (creator) at the top if they're eligible
    // This ensures the dropdown is NEVER empty when creating a user
    if (!editingUser && currentUser?.id && currentUser?.role) {
      const currentUserRoleLower = (currentUser.role || '').toLowerCase();
      const isEligible = eligibleRoles.some(r => r.toLowerCase() === currentUserRoleLower);
      
      // #region agent log
      console.log('[ADMIN] Checking if should add current user:', {
        isCreating: !editingUser,
        currentUserId: currentUser.id,
        currentUserRole: currentUser.role,
        currentUserRoleLower,
        isEligible,
        currentUserInFiltered: !!filtered.find(u => u.id === currentUser.id)
      });
      // #endregion
      
      if (isEligible) {
        // Remove current user from list if already there (we'll add to top)
        filtered = filtered.filter(u => u.id !== currentUser.id);
        
        // Create current user object (they might not be in API response)
        const creatorUser: User = {
          id: currentUser.id,
          name: currentUser.name || currentUser.email || 'You',
          email: currentUser.email || '',
          role: currentUser.role,
          reports_to: undefined,
          reports_to_name: undefined,
          preferred_currency: currentUser.preferred_currency || 'INR',
          notification_email: currentUser.email,
          is_active: true,
          projects: [],
          last_login_at: undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // ALWAYS add current user at the TOP
        filtered = [creatorUser, ...filtered];
        
        // #region agent log
        console.log('[ADMIN] ✅ Added current user to TOP of managers list:', creatorUser);
        // #endregion
      } else {
        // #region agent log
        console.log('[ADMIN] ❌ Current user is NOT eligible - role:', currentUser.role, 'not in', eligibleRoles);
        // #endregion
      }
    } else {
      // #region agent log
      console.log('[ADMIN] ⚠️ Skipping current user addition:', {
        isEditing: !!editingUser,
        hasCurrentUserId: !!currentUser?.id,
        hasCurrentUserRole: !!currentUser?.role,
        currentUser: currentUser
      });
      // #endregion
    }
    
    // #region agent log
    console.log('[ADMIN] Final potentialManagers:', {
      count: filtered.length,
      managers: filtered.map(m => ({ id: m.id, name: m.name, role: m.role }))
    });
    // #endregion
    
    return filtered;
  }, [users, editingUser, currentUser]);

  const availableRoles = useMemo(() => {
    if (currentUser?.role === 'superadmin') {
      // Superadmin can create any role
      return [
        { value: 'superadmin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin' },
        { value: 'leadership', label: 'Leadership' },
        { value: 'sales', label: 'Sales' },
        { value: 'bd', label: 'Business Development' },
      ];
    } else if (currentUser?.role === 'admin') {
      // Admin can only create: admin, bd, or sales (not superadmin, not leadership)
      return [
        { value: 'admin', label: 'Admin' },
        { value: 'bd', label: 'Business Development' },
        { value: 'sales', label: 'Sales' },
      ];
    } else {
      // Other roles shouldn't be creating users, but just in case
      return [
        { value: 'bd', label: 'Business Development' },
      ];
    }
  }, [currentUser]);

  const createMutation = useMutation({
    mutationFn: (data: any) => {
      console.log('Creating user with data:', { ...data, password: data.password ? '(set)' : '(not set)' });
      return adminApi.createUser(data);
    },
    onSuccess: (data: any) => {
      console.log('User created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowModal(false);
      const creds = createdCredentialsRef.current;
      createdCredentialsRef.current = null;
      resetForm();
      if (creds && data?.credentials_set_by_admin) {
        alert(`User created successfully!\n\nShare these login credentials with the user internally:\n\nEmail: ${creds.email}\nPassword: ${creds.password}\n\n(They can change the password after first login.)`);
      } else {
        alert('User created successfully! An email has been sent with login instructions.');
      }
    },
    onError: (error: any) => {
      console.error('Create user error details:', {
        error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create user. Please check all fields.';
      alert(`Error: ${errorMessage}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<User> }) => {
      return adminApi.updateUser(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      alert('User updated successfully!');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update user. Please check all fields.';
      alert(errorMessage);
      console.error('Update user error:', error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: any) => {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to delete user.';
        alert(errorMessage);
    }
  });

  // SMTP Settings mutations
  const updateSMTPMutation = useMutation({
    mutationFn: adminApi.updateSMTPSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtp-settings'] });
      setShowSMTPModal(false);
      alert('SMTP settings updated successfully!');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update SMTP settings.';
      alert(errorMessage);
    },
  });

  const testSMTPMutation = useMutation({
    mutationFn: (to: string) => adminApi.testSMTP(to),
    onSuccess: () => {
      alert('Test email sent successfully! Please check your inbox.');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send test email.';
      alert(errorMessage);
    },
  });

  const resetForm = () => {
    // Default reports_to to current user (creator) if they are eligible to be a manager
    // This ensures new users report to the person creating their account
    const eligibleRoles = ['superadmin', 'admin', 'leadership'];
    const currentUserRoleLower = (currentUser?.role || '').toLowerCase();
    const isEligible = currentUser?.role && eligibleRoles.some(r => r.toLowerCase() === currentUserRoleLower);
    const defaultReportsTo = (isEligible && currentUser?.id) ? currentUser.id : undefined;
    
    // #region agent log
    console.log('[ADMIN] resetForm - setting default reports_to:', {
      currentUserId: currentUser?.id,
      currentUserName: currentUser?.name,
      currentUserRole: currentUser?.role,
      currentUserRoleLower,
      isEligible,
      defaultReportsTo
    });
    // #endregion
    
    setFormData({
      email: '',
      name: '',
      role: 'bd',
      preferred_currency: 'INR',
      notification_email: '',
      reports_to: defaultReportsTo,
      is_active: true,
      create_with_password: false,
      password: '',
      confirm_password: '',
    });
    setSelectedProjectIds([]);
    createdCredentialsRef.current = null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      email: formData.email,
      name: formData.name,
      role: formData.role,
      preferred_currency: formData.preferred_currency,
      notification_email: formData.notification_email || undefined,
      reports_to: formData.reports_to ? parseInt(String(formData.reports_to)) : undefined,
      is_active: formData.is_active,
      project_ids: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
    };
    if (!payload.notification_email || payload.notification_email.trim() === '') {
      payload.notification_email = undefined;
    }

    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: payload });
      return;
    }

    // Create user
    if (formData.create_with_password) {
      const p = (formData.password || '').trim();
      const cp = (formData.confirm_password || '').trim();
      if (p.length < 6) {
        alert('Password must be at least 6 characters.');
        return;
      }
      if (p !== cp) {
        alert('Password and Confirm password do not match.');
        return;
      }
      payload.password = p;
      createdCredentialsRef.current = { email: formData.email, password: p };
    }
    createMutation.mutate(payload);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      preferred_currency: user.preferred_currency || 'INR',
      notification_email: (user as any).notification_email || user.email || '',
      reports_to: user.reports_to,
      is_active: user.is_active !== false, // Default to true if undefined
      create_with_password: false,
      password: '',
      confirm_password: '',
    });
    // Set selected projects if user has projects
    if (user.projects && Array.isArray(user.projects)) {
      setSelectedProjectIds(user.projects.map(p => p.id));
    } else {
      setSelectedProjectIds([]);
    }
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Admin Panel">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Admin Panel"
      subtitle="Manage users, hierarchy, and assignments"
    >
      <div className="space-y-6">
        {/* Actions - Only show Create User button for superadmin and admin */}
        {(() => {
          const canCreateUser = currentUser?.role === 'superadmin' || currentUser?.role === 'admin';
          // #region agent log
          console.log('[ADMIN] Checking if user can create users:', {
            currentUserRole: currentUser?.role,
            canCreateUser
          });
          // #endregion
          
          if (!canCreateUser) {
            return null; // Hide button for BD, sales, and other roles
          }
          
          return (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create User
              </button>
            </div>
          );
        })()}

        {/* Users List */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <ShieldCheckIcon className="h-6 w-6 mr-2" />
            User Management
          </h2>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <UserCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reports To</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assigned Projects</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Login</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <span className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-800 font-bold">
                                {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 capitalize">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {user.reports_to_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_active !== false ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="w-3 h-3 mr-1" /> Active
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircleIcon className="w-3 h-3 mr-1" /> Inactive
                            </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {user.projects && Array.isArray(user.projects) && user.projects.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.projects.slice(0, 3).map((project) => (
                              <span
                                key={project.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {project.name}
                              </span>
                            ))}
                            {user.projects.length > 3 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                    +{user.projects.length - 3} more
                                </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No projects</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={user.id === currentUser?.id}
                          title={user.id === currentUser?.id ? "Cannot delete yourself" : "Delete user"}
                        >
                          <TrashIcon className={`h-5 w-5 ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SMTP Settings Section (Superadmin Only) */}
        {currentUser?.role === 'superadmin' && (
          <div className="premium-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <EnvelopeIcon className="h-6 w-6 mr-2" />
                SMTP Email Configuration
              </h2>
              <button
                onClick={() => setShowSMTPModal(true)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                {smtpSettings ? 'Edit Settings' : 'Configure SMTP'}
              </button>
            </div>
            
            {smtpLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
              </div>
            ) : smtpSettings ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SMTP Host</label>
                    <p className="mt-1 text-sm text-gray-900">{smtpSettings.smtp_host}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SMTP Port</label>
                    <p className="mt-1 text-sm text-gray-900">{smtpSettings.smtp_port}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">SMTP User</label>
                    <p className="mt-1 text-sm text-gray-900">{smtpSettings.smtp_user}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">From Email</label>
                    <p className="mt-1 text-sm text-gray-900">{smtpSettings.email_from}</p>
                  </div>
                  {smtpSettings.escalation_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Escalation Email</label>
                      <p className="mt-1 text-sm text-gray-900">{smtpSettings.escalation_email}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="mt-1">
                      {smtpSettings.is_active !== false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="w-3 h-3 mr-1" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="w-3 h-3 mr-1" /> Inactive
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setTestEmail('');
                      const modal = document.getElementById('test-smtp-modal');
                      if (modal) {
                        (modal as any).showModal();
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                    Test SMTP Connection
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <EnvelopeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No SMTP settings configured. Using environment variables.</p>
                <button
                  onClick={() => setShowSMTPModal(true)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
                >
                  Configure SMTP Settings
                </button>
              </div>
            )}
          </div>
        )}

        {/* SMTP Settings Modal */}
        {showSMTPModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-4 pb-4 px-4 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">SMTP Email Configuration</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateSMTPMutation.mutate(smtpFormData);
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SMTP Host <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={smtpFormData.smtp_host}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, smtp_host: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="smtp.gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SMTP Port <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={smtpFormData.smtp_port}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, smtp_port: parseInt(e.target.value) || 587 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="587"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">Common: 587 (TLS), 465 (SSL)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SMTP User/Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={smtpFormData.smtp_user}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, smtp_user: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="your-email@gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SMTP Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={smtpFormData.smtp_password}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, smtp_password: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder={smtpSettings ? 'Leave blank to keep current' : 'Enter password'}
                      required={!smtpSettings}
                    />
                    <p className="mt-1 text-xs text-gray-500">For Gmail, use App Password</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      From Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={smtpFormData.email_from}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, email_from: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="noreply@pulpstrategy.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Escalation Email
                    </label>
                    <input
                      type="email"
                      value={smtpFormData.escalation_email}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, escalation_email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="senior@pulpstrategy.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      IMAP Host (Optional)
                    </label>
                    <input
                      type="text"
                      value={smtpFormData.imap_host}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, imap_host: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="imap.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      IMAP Port (Optional)
                    </label>
                    <input
                      type="number"
                      value={smtpFormData.imap_port}
                      onChange={(e) => setSmtpFormData({ ...smtpFormData, imap_port: parseInt(e.target.value) || 993 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="993"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="smtp_active"
                    checked={smtpFormData.is_active}
                    onChange={(e) => setSmtpFormData({ ...smtpFormData, is_active: e.target.checked })}
                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="smtp_active" className="text-sm text-gray-700">
                    SMTP settings are active
                  </label>
                </div>
                <div className="flex space-x-4 pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 font-medium"
                    disabled={updateSMTPMutation.isPending}
                  >
                    {updateSMTPMutation.isPending ? 'Saving...' : 'Save SMTP Settings'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSMTPModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Test SMTP Modal */}
        <dialog id="test-smtp-modal" className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Test SMTP Connection</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Test Email Address
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="your-email@example.com"
                />
              </div>
              <div className="modal-action">
                <button
                  onClick={() => {
                    if (testEmail) {
                      testSMTPMutation.mutate(testEmail);
                    } else {
                      alert('Please enter an email address');
                    }
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  disabled={testSMTPMutation.isPending}
                >
                  {testSMTPMutation.isPending ? 'Sending...' : 'Send Test Email'}
                </button>
                <form method="dialog">
                  <button className="btn">Close</button>
                </form>
              </div>
            </div>
          </div>
        </dialog>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-4 pb-4 px-4 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingUser ? 'Edit User' : 'Create User'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        required
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        required
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Role <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={formData.role}
                        onChange={(e) => {
                            setFormData({ ...formData, role: e.target.value });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        disabled={currentUser?.role !== 'superadmin' && editingUser?.role === 'superadmin'}
                    >
                        {availableRoles.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                    </select>
                    </div>
                    <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Reports To
                    </label>
                    <select
                        value={formData.reports_to || ''}
                        onChange={(e) => setFormData({ ...formData, reports_to: e.target.value ? parseInt(e.target.value) : undefined })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                        <option value="">Select Manager (Optional)...</option>
                        {(() => {
                          // Always ensure current user is available when creating
                          const eligibleRoles = ['superadmin', 'admin', 'leadership'];
                          const currentUserRoleLower = (currentUser?.role || '').toLowerCase();
                          const currentUserEligible = currentUser?.id && currentUser?.role && 
                            eligibleRoles.some(r => r.toLowerCase() === currentUserRoleLower);
                          
                          // #region agent log
                          console.log('[ADMIN] Dropdown render:', {
                            potentialManagersCount: potentialManagers.length,
                            currentUserId: currentUser?.id,
                            currentUserName: currentUser?.name,
                            currentUserEmail: currentUser?.email,
                            currentUserRole: currentUser?.role,
                            currentUserEligible,
                            isEditing: !!editingUser,
                            formDataReportsTo: formData.reports_to,
                            potentialManagers: potentialManagers.map(m => ({ id: m.id, name: m.name, role: m.role }))
                          });
                          // #endregion
                          
                          // If creating and current user is eligible, ensure they're in the list
                          if (!editingUser && currentUserEligible) {
                            const currentUserInList = potentialManagers.find(m => m.id === currentUser.id);
                            
                            if (!currentUserInList) {
                              // Current user should be in list but isn't - add them as fallback
                              // #region agent log
                              console.log('[ADMIN] ⚠️ Current user eligible but NOT in potentialManagers - adding as fallback');
                              // #endregion
                              return (
                                <>
                                  <option key={currentUser.id} value={currentUser.id}>
                                    {currentUser.name || currentUser.email || 'You'} ({currentUser.role}) - You (Default)
                                  </option>
                                  {potentialManagers.map(manager => (
                                    <option key={manager.id} value={manager.id}>
                                      {manager.name} ({manager.role})
                                    </option>
                                  ))}
                                </>
                              );
                            }
                          }
                          
                          // If list is empty but current user is eligible, show them
                          if (potentialManagers.length === 0 && currentUserEligible && !editingUser) {
                            // #region agent log
                            console.log('[ADMIN] ⚠️ potentialManagers is empty but current user is eligible - showing fallback');
                            // #endregion
                            return (
                              <option key={currentUser.id} value={currentUser.id}>
                                {currentUser.name || currentUser.email || 'You'} ({currentUser.role}) - You (Default)
                              </option>
                            );
                          }
                          
                          // Show all managers
                          return potentialManagers.map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name} ({manager.role})
                              {manager.id === currentUser?.id ? ' - You (Default)' : ''}
                            </option>
                          ));
                        })()}
                    </select>
                    {(() => {
                      const eligibleRoles = ['superadmin', 'admin', 'leadership'];
                      const currentUserRoleLower = (currentUser?.role || '').toLowerCase();
                      const currentUserEligible = currentUser?.id && currentUser?.role && 
                        eligibleRoles.some(r => r.toLowerCase() === currentUserRoleLower);
                      
                      if (potentialManagers.length === 0 && currentUserEligible && !editingUser) {
                        return (
                          <p className="mt-1 text-xs text-green-600">
                            ✓ {currentUser.name || currentUser.email} will be set as the manager by default.
                          </p>
                        );
                      }
                      if (potentialManagers.length === 0 && !currentUserEligible) {
                        return (
                          <p className="mt-1 text-xs text-gray-500">
                            No eligible managers found. User will not have a reporting manager.
                            {currentUser?.role && (
                              <span className="block mt-1">Your role: {currentUser.role} (needs to be: {eligibleRoles.join(', ')})</span>
                            )}
                          </p>
                        );
                      }
                      return null;
                    })()}
                    </div>
                    <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Preferred Currency
                    </label>
                    <select
                        value={formData.preferred_currency}
                        onChange={(e) => setFormData({ ...formData, preferred_currency: e.target.value as 'USD' | 'INR' })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                        <option value="INR">INR (₹)</option>
                    </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Status
                        </label>
                        <div className="flex items-center space-x-2 mt-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <label htmlFor="is_active" className="text-sm text-gray-700">
                                Account is active
                            </label>
                        </div>
                    </div>
                </div>

                {!editingUser && (
                  <>
                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
                      <input
                        type="checkbox"
                        id="create_with_password"
                        checked={formData.create_with_password}
                        onChange={(e) => setFormData({ ...formData, create_with_password: e.target.checked, password: '', confirm_password: '' })}
                        className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="create_with_password" className="text-sm font-medium text-gray-700">
                        Set password and share credentials internally (no email sent)
                      </label>
                    </div>
                    {formData.create_with_password && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Password <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Min 6 characters"
                            minLength={6}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Confirm Password <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            value={formData.confirm_password}
                            onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Re-enter password"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Notification Email
                    <span className="text-gray-500 text-xs ml-2">(Optional)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.notification_email}
                    onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enter email for notifications if different from login email"
                  />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Assign Projects (Accounts/Brands)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {accounts.length === 0 ? (
                        <p className="text-sm text-gray-500">No accounts available</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {accounts.map((account) => (
                            <label
                              key={account.id}
                              className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded border border-transparent hover:border-gray-200"
                            >
                              <input
                                type="checkbox"
                                checked={selectedProjectIds.includes(account.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedProjectIds([...selectedProjectIds, account.id]);
                                  } else {
                                    setSelectedProjectIds(selectedProjectIds.filter(id => id !== account.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm text-gray-700 truncate" title={account.name}>
                                {account.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedProjectIds.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        {selectedProjectIds.length} project{selectedProjectIds.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                </div>

                <div className="flex space-x-4 pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 font-medium"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingUser(null);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Admin;
